/**
 * The pool's copy in IndexedDB, so a reload renders from the last session
 * instead of waiting on the network (back-end-rs/docs/SYNC_ENGINE.md §7).
 *
 * **One database per user, deleted on sign-out.** This is a replica of
 * company data; it must not outlive the session that was allowed to see
 * it, and one person's copy must never be read into another's.
 *
 * Every call degrades to a no-op where IndexedDB is unavailable (tests,
 * private modes that refuse it): the app then bootstraps every load.
 */
import { ModelArrays, SCHEMA } from './models';

const PREFIX = 'levittown-sync-';
const STORE = 'state';
const KEY = 'snapshot';

export interface Persisted {
  schema: number;
  lastSyncId: number;
  models: ModelArrays;
  savedAt: number;
}

const available = (): boolean => typeof indexedDB !== 'undefined';

const open = (userId: string): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(PREFIX + userId, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export async function load(userId: string): Promise<Persisted | null> {
  if (!available()) return null;
  try {
    const db = await open(userId);
    const value = await new Promise<Persisted | undefined>((resolve, reject) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result as Persisted | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();
    // Written by another version of the client: its shape may not match.
    return value && value.schema === SCHEMA ? value : null;
  } catch {
    return null;
  }
}

export async function save(
  userId: string,
  data: { lastSyncId: number; models: ModelArrays }
): Promise<void> {
  if (!available()) return;
  try {
    const db = await open(userId);
    const value: Persisted = { schema: SCHEMA, savedAt: Date.now(), ...data };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // Losing the cache costs one bootstrap on the next load, nothing more.
  }
}

const drop = (name: string): Promise<void> =>
  new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });

/**
 * Delete every user's copy except `keepUserId`'s. `indexedDB.databases()`
 * is what finds copies left by someone who signed out by closing the tab;
 * where it is missing, at least the named user's copy goes.
 */
export async function removeAll(keepUserId?: string, knownUserId?: string): Promise<void> {
  if (!available()) return;
  const names = new Set<string>();
  try {
    const list = await (indexedDB as IDBFactory & {
      databases?: () => Promise<{ name?: string }[]>;
    }).databases?.();
    list?.forEach((d) => d.name?.startsWith(PREFIX) && names.add(d.name));
  } catch {
    // Not supported: fall through to the one we know about.
  }
  if (knownUserId) names.add(PREFIX + knownUserId);
  if (keepUserId) names.delete(PREFIX + keepUserId);
  await Promise.all(Array.from(names).map(drop));
}

const WRITES_KEY = 'writes';

/**
 * Writes this tab made that the server has not yet accepted
 * (`writes.ts`), so a reload — or a laptop closed on a dead network —
 * sends them later instead of losing them. Same database, so signing out
 * deletes them with the copy.
 */
export async function loadWrites<T>(userId: string): Promise<T[]> {
  if (!available()) return [];
  try {
    const db = await open(userId);
    const value = await new Promise<{ schema: number; writes: T[] } | undefined>(
      (resolve, reject) => {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(WRITES_KEY);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }
    );
    db.close();
    return value && value.schema === SCHEMA ? value.writes : [];
  } catch {
    return [];
  }
}

export async function saveWrites<T>(userId: string, writes: T[]): Promise<void> {
  if (!available()) return;
  try {
    const db = await open(userId);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ schema: SCHEMA, writes }, WRITES_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // The writes are still in memory and still being sent.
  }
}
